import React from 'react';
import { Loader2 } from 'lucide-react';

/** The Steam mark, inlined so the button has no network dependency. */
export function SteamIcon({ className = 'size-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M11.98 0C5.66 0 .48 4.87 0 11.06l6.44 2.66a3.4 3.4 0 0 1 1.92-.59l.17.01 2.86-4.14v-.06a4.55 4.55 0 1 1 4.55 4.55h-.11L11.75 16.3v.14a3.42 3.42 0 0 1-6.79.6L.35 15.13A12 12 0 1 0 11.98 0zm-4.4 18.2.98.4a2.58 2.58 0 0 0 3.4-3.98 2.58 2.58 0 0 0-2.83-.5l1.02.42a1.9 1.9 0 1 1-1.46 3.51l-1.1-.45zM18.98 9.4a3.03 3.03 0 1 0-6.06 0 3.03 3.03 0 0 0 6.06 0zm-5.3 0a2.28 2.28 0 1 1 4.55 0 2.28 2.28 0 0 1-4.56 0z" />
    </svg>
  );
}

/**
 * The one Steam call to action, shared by the sign-in screen and the link gate.
 *
 * Renders an <a> rather than a <button> when given an href, because a Steam
 * login is a real top-level navigation — Steam serves its own page, sets
 * X-Frame-Options, and may stop for a Steam Guard prompt. It cannot be fetched
 * and it cannot be framed. The link flow has to mint a ticket first, so it
 * passes onClick instead and navigates itself.
 */
export default function SteamButton({ href, onClick, loading = false, children = 'Sign in through Steam', className = '' }) {
  const shared =
    'group relative flex w-full items-center justify-center gap-2.5 rounded-xl border border-[#66c0f4]/25 '
    + 'bg-gradient-to-b from-[#1b2838] to-[#171a21] px-4 py-3 text-sm font-semibold text-[#c7d5e0] '
    + 'shadow-[0_10px_30px_-12px_rgba(0,0,0,0.9)] transition-all '
    + 'hover:border-[#66c0f4]/50 hover:text-white hover:shadow-[0_12px_34px_-12px_rgba(102,192,244,0.45)] '
    + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#66c0f4]/50 '
    + 'disabled:pointer-events-none disabled:opacity-60 '
    + className;

  const inner = (
    <>
      {loading
        ? <Loader2 className="size-5 animate-spin" />
        : <SteamIcon className="size-5 text-[#66c0f4] transition-colors group-hover:text-[#8fd3ff]" />}
      <span>{loading ? 'Contacting Steam…' : children}</span>
    </>
  );

  if (href) return <a href={href} className={shared}>{inner}</a>;

  return (
    <button type="button" onClick={onClick} disabled={loading} className={shared}>
      {inner}
    </button>
  );
}
